import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Spinner } from './components/ui';
import { AppLayout } from './components/layout/AppLayout';
import { DashboardPage } from './pages/DashboardPage';
import { BrandsPage } from './pages/BrandsPage';
import { ContentStudioPage } from './pages/ContentStudioPage';
import { IdeasPage } from './pages/IdeasPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CalendarPage } from './pages/CalendarPage';
import { ReviewQueuePage } from './pages/ReviewQueuePage';
import { PublishingPage } from './pages/PublishingPage';
import { MediaLibraryPage } from './pages/MediaLibraryPage';
import { ImageStudioPage } from './pages/ImageStudioPage';
import { AiRoutingPage } from './pages/AiRoutingPage';
import { AiCostsPage } from './pages/AiCostsPage';
import { VideoJobsPage } from './pages/VideoJobsPage';
import { PinsPage } from './pages/PinsPage';
import { PinsContentPage } from './pages/PinsContentPage';
import { PinsLongVideoPage } from './pages/PinsLongVideoPage';
import { RenderFarmPage } from './pages/RenderFarmPage';
import { VideoTemplatesPage } from './pages/VideoTemplatesPage';
import { SourcesPage } from './pages/SourcesPage';
import { BrandBrainPage } from './pages/BrandBrainPage';
import { SocialAccountsPage } from './pages/SocialAccountsPage';
import { AiProvidersPage } from './pages/AiProvidersPage';
import { PromptLibraryPage } from './pages/PromptLibraryPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { CostsPage } from './pages/CostsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ContentDetailPage } from './pages/ContentDetailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/LoginPage';
import { FboPage } from './pages/FboPage';
import { FboFinanceStudioPage } from './pages/FboFinanceStudioPage';
import { RemotionLabPage } from './pages/RemotionLabPage';
import { BloodCrimesPage } from './pages/BloodCrimesPage';
import { RiftCellsGameplayPage } from './pages/RiftCellsGameplayPage';
import { RiftCellsEducationalPage } from './pages/RiftCellsEducationalPage';
import { BloodCrimesCityReelsPage } from './pages/BloodCrimesCityReelsPage';
import { BloodCrimesYouTubePage } from './pages/BloodCrimesYouTubePage';
import { WboStudioPage } from './pages/WboStudioPage';
import { BloodCrimesTvPage } from './pages/BloodCrimesTvPage';
import { ComedyStudioPage } from './pages/comedy/ComedyStudioPage';
import { NieboGuardPage } from './pages/NieboGuardPage';
import { DistributionHubPage } from './pages/DistributionHubPage';

function Gate() {
  const { user, checking, refresh } = useAuth();

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <Spinner label="Sprawdzanie sesji…" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="brands" element={<BrandsPage />} />
        <Route path="fbo" element={<FboPage />} />
        <Route path="wbo" element={<WboStudioPage />} />
        <Route path="fbo/finance-studio" element={<FboFinanceStudioPage />} />
        <Route path="bloodcrimes" element={<BloodCrimesPage />} />
        <Route path="bloodcrimes/city-reels" element={<BloodCrimesCityReelsPage />} />
        <Route path="bloodcrimes/youtube" element={<BloodCrimesYouTubePage />} />
        <Route path="bloodcrimes/tv" element={<BloodCrimesTvPage />} />
        <Route path="comedy" element={<ComedyStudioPage />} />
        <Route path="nieboguard" element={<NieboGuardPage />} />
        <Route path="distribution" element={<DistributionHubPage />} />
        <Route path="riftcells/gameplay" element={<RiftCellsGameplayPage />} />
        <Route path="riftcells/longform" element={<RiftCellsGameplayPage view="longform" />} />
        <Route path="riftcells/educational" element={<RiftCellsEducationalPage />} />
        <Route path="remotion-lab" element={<RemotionLabPage />} />
        {/* Backward-compatible FBO deep link. */}
        <Route path="fbo/remotion-lab" element={<RemotionLabPage />} />
        <Route path="content-studio" element={<ContentStudioPage />} />
        <Route path="image-studio" element={<ImageStudioPage />} />
        <Route path="ai-routing" element={<AiRoutingPage />} />
        <Route path="ai-costs" element={<AiCostsPage />} />
        <Route path="video-jobs" element={<VideoJobsPage />} />
        <Route path="pins" element={<PinsPage />} />
        <Route path="pins/long-video" element={<PinsLongVideoPage />} />
        <Route path="pins-content" element={<PinsContentPage />} />
        <Route path="render-farm" element={<RenderFarmPage />} />
        <Route path="video-templates" element={<VideoTemplatesPage />} />
        <Route path="content/:id" element={<ContentDetailPage />} />
        <Route path="ideas" element={<IdeasPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="review" element={<ReviewQueuePage />} />
        <Route path="publishing" element={<PublishingPage />} />
        <Route path="media" element={<MediaLibraryPage />} />
        <Route path="sources" element={<SourcesPage />} />
        <Route path="brand-brain" element={<BrandBrainPage />} />
        <Route path="social-accounts" element={<SocialAccountsPage />} />
        <Route path="ai-providers" element={<AiProvidersPage />} />
        <Route path="prompts" element={<PromptLibraryPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="costs" element={<CostsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  const isPreviewHost = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location.hostname);
  return (
    <BrowserRouter>
      <AuthProvider>
        {isPreviewHost && <div className="alert alert--error" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000, textAlign: 'center' }}>To jest adres podglądu wdrożenia (*.pages.dev) — nie przekazuje /api. Otwórz <a href="https://kontentowo.nullexa.eu">https://kontentowo.nullexa.eu</a>.</div>}
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}
